import React, { useState, useEffect } from 'react';
import { Bell, ChevronRight, X, Loader2 } from 'lucide-react';
import { getUserNotifications, markNotificationsAsSeen } from '../../../services/notificationService';
import { useAuth } from '../../../contexts/AuthContext';

const NotificationsPreview = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const { user } = useAuth();

  // Fetch recent 10 notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const result = await getUserNotifications(user?.id, 10);
        if (result?.success) {
          setNotifications(result?.data || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user?.id]);

  // Fetch all notifications when modal opens
  const handleViewAll = async () => {
    setShowModal(true);
    setModalLoading(true);
    
    try {
      const result = await getUserNotifications(user?.id, 1000); // Fetch all notifications
      if (result?.success) {
        setAllNotifications(result?.data || []);
        
        // Mark unseen notifications as seen
        const unseenIds = result?.data?.filter(n => !n?.seen)?.map(n => n?.id);
        if (unseenIds?.length > 0) {
          await markNotificationsAsSeen(user?.id, unseenIds);
          // Update local state
          setNotifications(prev => prev?.map(n => ({ ...n, seen: true })));
        }
      }
    } catch (error) {
      console.error('Error fetching all notifications:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    const iconClass = "w-5 h-5";
    switch (type) {
      case 'task_created': case'task_status_changed':
        return <Bell className={iconClass} />;
      case 'meeting_scheduled':
        return <Bell className={iconClass} />;
      case 'file_uploaded':
        return <Bell className={iconClass} />;
      case 'announcement':
        return <Bell className={iconClass} />;
      default:
        return <Bell className={iconClass} />;
    }
  };

  const NotificationItem = ({ notification, showDivider = true }) => (
    <div className={`${showDivider ? 'border-b border-border' : ''}`}>
      <div className={`p-4 hover:bg-accent/50 transition-colors ${!notification?.seen ? 'bg-blue-50/50' : ''}`}>
        <div className="flex gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            !notification?.seen ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
          }`}>
            {getNotificationIcon(notification?.type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-foreground line-clamp-1">
                {notification?.title}
              </h4>
              {!notification?.seen && (
                <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1.5"></span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {notification?.message}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {formatDate(notification?.created_at)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Recent Notifications</h3>
          </div>
          <button
            onClick={handleViewAll}
            className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : notifications?.length > 0 ? (
            notifications?.map((notification, index) => (
              <NotificationItem
                key={notification?.id}
                notification={notification}
                showDivider={index < notifications?.length - 1}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Bell className="w-12 h-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No notifications yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                You'll see updates about tasks, meetings, and files here
              </p>
            </div>
          )}
        </div>
      </div>

      {/* View All Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">All Notifications</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {modalLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
              ) : allNotifications?.length > 0 ? (
                allNotifications?.map((notification, index) => (
                  <NotificationItem
                    key={notification?.id}
                    notification={notification}
                    showDivider={index < allNotifications?.length - 1}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No notifications yet</p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    You'll see updates about tasks, meetings, and files here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationsPreview;